"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { Search, BookOpen, ChevronRight, Star } from "lucide-react";
import {
  listCategories,
  articlesByCategory,
  popularArticles,
  searchKb,
  type KbArticle,
} from "@/lib/help/articles";
import { useI18n } from "@/lib/i18n/I18nProvider";
import { localizeArticle, localizeCategory, ui, pluralArticlesL } from "@/lib/help/localized";

/** Главная база знаний: поиск + категории + популярные статьи.
 *  `basePath`: префикс ссылок на статьи ("/help" — кабинет, "/knowledge" — публичная часть). */
export function KnowledgeBaseClient({ basePath = "/help" }: { basePath?: string } = {}) {
  const { locale } = useI18n();
  const [query, setQuery] = useState("");
  const categories = useMemo(() => listCategories().map((c) => localizeCategory(c, locale)), [locale]);
  const popular = useMemo(() => popularArticles().map((a) => localizeArticle(a, locale)), [locale]);
  const results = useMemo(() => searchKb(query).map((a) => localizeArticle(a, locale)), [query, locale]);
  const searching = query.trim().length > 0;

  return (
    <div className="space-y-6">
      <header className="flex items-start gap-3">
        <div className="rounded-xl bg-keiro-mist/60 p-2.5 text-keiro-green">
          <BookOpen className="h-6 w-6" aria-hidden />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-keiro-obsidian">{ui("title", locale)}</h1>
          <p className="mt-0.5 text-sm text-keiro-gray-600">{ui("subtitle", locale)}</p>
        </div>
      </header>

      <div className="relative max-w-2xl">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-keiro-gray-400" aria-hidden />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={ui("searchPlaceholder", locale)}
          className="w-full rounded-xl border border-keiro-green/15 bg-white py-3 pl-10 pr-4 text-sm text-keiro-obsidian shadow-sm outline-none placeholder:text-keiro-gray-400 focus:border-keiro-green/40 focus:ring-2 focus:ring-keiro-green/15"
          aria-label="Поиск по базе знаний"
        />
      </div>

      {searching ? (
        <section aria-label="Результаты поиска">
          <p className="mb-3 text-sm text-keiro-gray-600">
            {results.length > 0
              ? `${ui("found", locale)} ${results.length}`
              : ui("nothingFound", locale)}
          </p>
          <ul className="space-y-2">
            {results.map((a) => (
              <ArticleRow key={a.slug} article={a} basePath={basePath} />
            ))}
          </ul>
        </section>
      ) : (
        <>
          <section aria-label="Разделы">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {categories.map((c) => {
                const count = articlesByCategory(c.id).length;
                return (
                  <Link
                    key={c.id}
                    href={`${basePath}#cat-${c.id}`}
                    className="group flex flex-col rounded-xl border border-keiro-green/10 bg-white p-4 shadow-sm transition-colors hover:border-keiro-green/30 hover:bg-keiro-mist/20"
                    scroll
                  >
                    <span className="text-sm font-semibold text-keiro-obsidian group-hover:text-keiro-green-dark">
                      {c.title}
                    </span>
                    <span className="mt-1 flex-1 text-xs leading-relaxed text-keiro-gray-600">{c.description}</span>
                    <span className="mt-3 text-caption font-medium uppercase tracking-wide text-keiro-gray-500">
                      {count} {pluralArticlesL(count, locale)}
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>

          {popular.length > 0 ? (
            <section aria-label="Популярные статьи">
              <h2 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-keiro-obsidian">
                <Star className="h-4 w-4 text-amber-500" aria-hidden /> {ui("popular", locale)}
              </h2>
              <ul className="space-y-2">
                {popular.map((a) => (
                  <ArticleRow key={a.slug} article={a} basePath={basePath} />
                ))}
              </ul>
            </section>
          ) : null}

          {categories.map((c) => {
            const items = articlesByCategory(c.id).map((a) => localizeArticle(a, locale));
            return (
              <section key={c.id} id={`cat-${c.id}`} aria-label={c.title} className="scroll-mt-20">
                <h2 className="mb-1 text-sm font-semibold text-keiro-obsidian">{c.title}</h2>
                <p className="mb-3 text-xs text-keiro-gray-600">{c.description}</p>
                <ul className="space-y-2">
                  {items.map((a) => (
                    <ArticleRow key={a.slug} article={a} basePath={basePath} />
                  ))}
                </ul>
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}

function ArticleRow({ article, basePath = "/help" }: { article: KbArticle; basePath?: string }) {
  return (
    <li>
      <Link
        href={`${basePath}/${article.slug}`}
        className="group flex items-center justify-between gap-3 rounded-lg border border-keiro-green/10 bg-white px-4 py-3 shadow-sm transition-colors hover:border-keiro-green/30 hover:bg-keiro-mist/20"
      >
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-keiro-obsidian group-hover:text-keiro-green-dark">
            {article.title}
          </span>
          {article.intro ? (
            <span className="mt-0.5 block truncate text-xs text-keiro-gray-600">{article.intro}</span>
          ) : null}
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-keiro-gray-400 group-hover:text-keiro-green" aria-hidden />
      </Link>
    </li>
  );
}

